/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import customerDataApi from "containers/Customers/api";
import {
  AccessGroupSearchResultContent,
  AccessGroupSearchParams,
} from "containers/Customers/types/Customers";
import { t } from "i18next";
import { action, makeObservable, observable } from "mobx";
import { persist } from "mobx-persist";
import { isEmpty } from "lodash";
import {
  getBusinessUsage,
  getChargeMode,
} from "containers/AccessManagement/helpers";
import { FilterOptions } from "containers/AccessManagement/types/AccessManagementTypes";
import { AuthStore } from "./AuthStore";
import { NotificationStore, AlertType } from "./NotificationStore";

export class AccessGroupStore {
  private authStore;
  private notificationStore;

  public constructor(auth: AuthStore, notification: NotificationStore) {
    this.authStore = auth;
    this.notificationStore = notification;
    makeObservable(this);
  }

  @observable @persist("object") public friendlyNameSearchResult:
    | AccessGroupSearchResultContent
    | undefined;
  @observable @persist("object") public friendlyNameSearchParams:
    | AccessGroupSearchParams
    | undefined;

  @observable @persist("object") public chipFilters = {
    usageFilters: { personal: false, business: false },
    chargeModeFilters: {
      eco: false,
      ecoPlus: false,
      fast: false,
      noneSelected: false,
    },
  };

  @observable @persist public isLoading = false;

  @action.bound
  public setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  public search = async (value = "") => {
    if (this.isLoading) return;

    try {
      this.setIsLoading(true);

      const currentPath = window.location.pathname;

      const pathParts = currentPath.split("/");
      const id = pathParts[pathParts.length - 1];

      const searchResults = await customerDataApi.getAccessGroupInfo(
        {
          filter: value,
          accessGroupId: id,
          IsBusinessUsage: getBusinessUsage(this.chipFilters.usageFilters),
          defaultChargeMode: getChargeMode(this.chipFilters.chargeModeFilters),
        },
        this.authStore.jwtToken,
      );

      if (isEmpty(searchResults.content.pinConfigs)) {
        this.notificationStore.triggerNotification(
          t("search.couldntFindFriendlyName", {
            searchTextValue: value,
          }),
          AlertType.warning,
        );
      }

      this.setAccessGroupSearchParams({ filter: value });
      this.setAccessGroupSearchResult(searchResults.content);
      this.setIsLoading(false);
    } catch {
      this.notificationStore.triggerNotification(
        t("search.serverError"),
        AlertType.error,
      );
    } finally {
      this.setIsLoading(false);
    }
  };

  @action.bound
  public setAccessGroupSearchResult(
    searchResult: AccessGroupSearchResultContent,
  ): void {
    this.friendlyNameSearchResult = searchResult;
  }

  @action.bound
  public setAccessGroupSearchParams(
    searchParams: AccessGroupSearchParams,
  ): void {
    this.friendlyNameSearchParams = searchParams;
  }
  @action.bound
  public setChipFilters(chipFilters: FilterOptions): void {
    this.chipFilters = chipFilters;
  }
}
